/**
 * Lightweight browser Notification helper.
 *
 * Scope (documented): fires notifications ONLY while the app is still open in a
 * background tab (WS connection alive). We do NOT implement Web Push (VAPID) —
 * with the tab fully closed, notifications will NOT arrive.
 */

const PROMPT_ASKED_KEY = "queonda.notif.prompt";

export function getPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // "granted" | "denied" | "default"
}

export async function requestPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  try {
    const p = await Notification.requestPermission();
    localStorage.setItem(PROMPT_ASKED_KEY, "1");
    return p;
  } catch {
    return "denied";
  }
}

export function hasBeenPrompted() {
  try {
    return !!localStorage.getItem(PROMPT_ASKED_KEY);
  } catch {
    return false;
  }
}

export function markPrompted() {
  try {
    localStorage.setItem(PROMPT_ASKED_KEY, "1");
  } catch (_e) {
    /* ignore */
  }
}

export function isDocumentHidden() {
  return typeof document !== "undefined" && (document.hidden || !document.hasFocus());
}

function truncate(s, max = 80) {
  if (!s) return "";
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + "…";
}

export async function showNotification({ title, body, tag, onClick }) {
  if (typeof Notification === "undefined") return null;
  if (Notification.permission !== "granted") return null;
  const bodyTrunc = truncate(body || "", 80);
  try {
    // Prefer service-worker registration for Android/iOS support if available
    if (
      "serviceWorker" in navigator &&
      (await navigator.serviceWorker.getRegistration?.())
    ) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg && reg.showNotification) {
        await reg.showNotification(title, {
          body: bodyTrunc,
          tag: tag || undefined,
          icon: "/icon-192.svg",
          badge: "/icon-192.svg",
        });
        return true;
      }
    }
    const n = new Notification(title, {
      body: bodyTrunc,
      tag: tag || undefined,
      icon: "/icon-192.svg",
    });
    if (onClick) {
      n.onclick = () => {
        try {
          window.focus();
        } catch (_e) {
          /* focus best-effort */
        }
        onClick();
        n.close();
      };
    }
    return n;
  } catch (_e) {
    return null;
  }
}
